import {
  applyTrendProcessingToMap,
  normalizeTrendMap,
  lowPassTrendMap,
} from '../../../shared/trend-processing';
import type {
  ChartViewId,
  CustomTimeSegment,
  SpectralWorkspaceSession,
  TrendSignalKey,
} from '../types';
import { aggregateTrendRows } from '../utils/aggregateTrend';
import {
  buildCustomSegmentValueMap,
  formatSegmentTabLabel,
} from '../utils/customSegment';
import { getCachedSessionValueMap } from '../utils/sessionValueMapCache';
import { filterSegmentRows } from '../utils/trendZoom';
import type { TrendExportContext } from './buildTrendExport';
import { buildTrendWideDataRows } from './buildTrendWideRows';

export type SessionTrendSeries = {
  sessionId: number;
  patientCode?: string | null;
  raw: Map<number, number>;
  normalized: Map<number, number> | null;
  lowPass: Map<number, number> | null;
  processed: Map<number, number>;
};

export const resolveViewLabel = (
  viewId: ChartViewId,
  customSegments: CustomTimeSegment[]
) => {
  if (viewId === 'full') return 'tratto-completo';

  const segment = customSegments.find(item => item.id === viewId);
  return segment ? formatSegmentTabLabel(segment) : viewId;
};

export const resolveViewSheetLabel = (
  viewId: ChartViewId,
  customSegments: CustomTimeSegment[]
) => {
  if (viewId === 'full') return 'Tratto completo';

  const segment = customSegments.find(item => item.id === viewId);
  return segment ? formatSegmentTabLabel(segment) : 'Tratto custom';
};

export const resolveTrendExportViewIds = (
  customSegments: CustomTimeSegment[]
): ChartViewId[] => ['full', ...customSegments.map(segment => segment.id)];

export const resolveTrendExportViewLabels = (
  customSegments: CustomTimeSegment[]
) =>
  resolveTrendExportViewIds(customSegments)
    .map(viewId => resolveViewLabel(viewId, customSegments))
    .join('; ');

export const resolveActiveOverlaySessionNumbers = (
  readySessions: SpectralWorkspaceSession[],
  showOverlaySessions: boolean,
  selectedOverlaySessionIds: string[]
) => {
  if (!showOverlaySessions) return [];

  const allIds = readySessions.map(session => session.sessionId.toString());
  const activeIds =
    selectedOverlaySessionIds.length === 0
      ? allIds
      : selectedOverlaySessionIds.filter(id => allIds.includes(id));

  return activeIds.map(Number).filter(Number.isFinite);
};

export const buildSessionTrendSeries = (
  session: SpectralWorkspaceSession,
  feature: TrendSignalKey,
  trendNormalizationEnabled: boolean,
  trendLowPassEnabled: boolean
): SessionTrendSeries | null => {
  const raw = getCachedSessionValueMap(
    session.sessionId,
    session.events ?? [],
    feature
  );

  if (raw.size === 0) return null;

  const normalized = trendNormalizationEnabled
    ? normalizeTrendMap(new Map(raw))
    : null;
  const lowPass = trendLowPassEnabled
    ? lowPassTrendMap(new Map(normalized ?? raw))
    : null;
  const processed = applyTrendProcessingToMap(new Map(raw), {
    normalized: trendNormalizationEnabled,
    lowPassFiltered: trendLowPassEnabled,
  });

  return {
    sessionId: session.sessionId,
    patientCode: session.patientCode,
    raw,
    normalized,
    lowPass,
    processed,
  };
};

const sliceSeriesForView = (
  series: SessionTrendSeries[],
  viewId: ChartViewId,
  customSegments: CustomTimeSegment[]
): SessionTrendSeries[] => {
  if (viewId === 'full') {
    return series;
  }

  const segment = customSegments.find(item => item.id === viewId);
  if (!segment) return [];

  const sliceMap = (valueMap: Map<number, number>) =>
    buildCustomSegmentValueMap(
      valueMap,
      segment.startSecond,
      segment.endSecond
    );

  return series
    .map(entry => ({
      ...entry,
      raw: sliceMap(entry.raw),
      normalized: entry.normalized ? sliceMap(entry.normalized) : null,
      lowPass: entry.lowPass ? sliceMap(entry.lowPass) : null,
      processed: sliceMap(entry.processed),
    }))
    .filter(entry => entry.processed.size > 0);
};

const aggregateSeries = (
  series: SessionTrendSeries[],
  pickValues: (entry: SessionTrendSeries) => Map<number, number>,
  overlaySessionIds: number[]
) =>
  aggregateTrendRows(
    series.map(entry => ({
      sessionId: entry.sessionId,
      values: pickValues(entry),
    })),
    overlaySessionIds
  );

export const computeTrendChartRowsForView = (
  context: TrendExportContext,
  feature: TrendSignalKey,
  viewId: ChartViewId
) => {
  const viewLabel = resolveViewLabel(viewId, context.customSegments);
  const overlaySessionIds = resolveActiveOverlaySessionNumbers(
    context.readySessions,
    context.showOverlaySessions,
    context.selectedOverlaySessionIds
  );

  const fullSeries = context.readySessions
    .map(session =>
      buildSessionTrendSeries(
        session,
        feature,
        context.trendNormalizationEnabled,
        context.trendLowPassEnabled
      )
    )
    .filter((entry): entry is SessionTrendSeries => entry != null);

  const series = sliceSeriesForView(fullSeries, viewId, context.customSegments);
  const overlayIds = context.showOverlaySessions ? overlaySessionIds : [];

  const chartRows = filterSegmentRows(
    aggregateSeries(series, entry => entry.processed, overlayIds),
    viewId === 'full' ? context.trendStart : null,
    viewId === 'full' ? context.trendEnd : null
  );

  return {
    viewLabel,
    overlaySessionIds,
    chartRows,
    series,
  };
};

export const buildTrendExportWideDataRows = (
  context: TrendExportContext,
  feature: TrendSignalKey,
  viewId: ChartViewId
) => {
  const { viewLabel, overlaySessionIds, chartRows, series } =
    computeTrendChartRowsForView(context, feature, viewId);

  const rawValuesBySessionId = new Map(
    series.map(entry => [entry.sessionId, entry.raw])
  );
  const overlayIds = context.showOverlaySessions
    ? overlaySessionIds.filter(id => rawValuesBySessionId.has(id))
    : [];

  return buildTrendWideDataRows(chartRows, {
    feature,
    viewLabel,
    segmentLabel: viewLabel,
    trendNormalizationEnabled: context.trendNormalizationEnabled,
    trendLowPassEnabled: context.trendLowPassEnabled,
    overlaySessionIds: overlayIds,
    resolveOverlayValue: (sessionId, second) =>
      rawValuesBySessionId.get(sessionId)?.get(second),
  });
};
